<script lang="ts">
  import { formatDate, formatAmount } from '../../lib/formatters';
  import type { RecurringTransaction } from '../../types/recurring';

  type Props = {
    items: RecurringTransaction[];
    onSnooze: (pattern: string, days: number) => Promise<void>;
    onArchive: (pattern: string) => Promise<void>;
    snoozingPattern: string | null;
    archivingPattern: string | null;
  };

  let { items, onSnooze, onArchive, snoozingPattern, archivingPattern }: Props = $props();
</script>

{#if items.length > 0}
  <div class="possibly-ended-section">
    <h2>⚠️ Possibly Cancelled Subscriptions</h2>
    <p class="section-description">
      These subscriptions haven't charged in over 1.5x their usual interval.
      Click "Still Active" to suppress this warning for 90 days, or "Archive" if truly cancelled.
    </p>
    <div class="possibly-ended-list">
      {#each items as item (item.pattern)}
        <div class="possibly-ended-item">
          <div class="item-name">{item.displayName}</div>
          <div class="item-details">
            <span class="amount">${formatAmount(item.avgAmount)}</span>
            <span class="separator">•</span>
            <span class="last-paid">Last paid: {formatDate(item.lastDate)}</span>
          </div>
          <div class="item-actions">
            <button
              class="snooze-btn"
              onclick={() => onSnooze(item.pattern, 90)}
              disabled={snoozingPattern === item.pattern || archivingPattern === item.pattern}
            >
              {snoozingPattern === item.pattern ? 'Snoozing...' : 'Still Active'}
            </button>
            <button
              class="archive-btn"
              onclick={() => onArchive(item.pattern)}
              disabled={archivingPattern === item.pattern || snoozingPattern === item.pattern}
            >
              {archivingPattern === item.pattern ? 'Archiving...' : 'Archive'}
            </button>
          </div>
        </div>
      {/each}
    </div>
  </div>
{/if}

<style lang="scss">
  .possibly-ended-section {
    margin-bottom: var(--spacing-2xl);
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--color-warning-light);
    border: 2px solid var(--color-warning);
    border-radius: var(--radius-lg);

    h2 {
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-semibold);
      color: var(--color-warning-dark);
      margin: 0 0 var(--spacing-sm) 0;
    }

    .section-description {
      color: var(--color-warning-darker);
      font-size: var(--font-size-sm);
      margin: 0 0 var(--spacing-md) 0;
    }
  }

  .possibly-ended-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--spacing-md);
  }

  .possibly-ended-item {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-md) var(--spacing-md);
    background: white;
    border: 1px solid var(--color-warning);
    border-radius: var(--radius-md);
    transition: all var(--transition-fast);

    &:hover {
      box-shadow: var(--shadow-lg);
    }

    .item-name {
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-sm);
      font-size: var(--font-size-sm);
    }

    .item-details {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      font-size: var(--font-size-xs);
      color: var(--color-text-tertiary);
      margin-bottom: var(--spacing-sm);

      .separator {
        color: var(--color-border-medium);
      }

      .amount {
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
      }
    }

    .item-actions {
      display: flex;
      gap: var(--spacing-sm);

      .snooze-btn {
        flex: 1;
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--color-success);
        color: white;
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-medium);
        cursor: pointer;
        transition: all var(--transition-fast);

        &:hover:not(:disabled) {
          background: var(--color-success-hover);
          transform: translateY(-1px);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      .archive-btn {
        flex: 1;
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--color-warning);
        color: white;
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-medium);
        cursor: pointer;
        transition: all var(--transition-fast);

        &:hover:not(:disabled) {
          background: var(--color-warning-hover);
          transform: translateY(-1px);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
</style>
